// ============================================================================
// sub-onboard-portal — client for the `sub-onboard` Edge Function (N3).
// Public (tokenized) calls carry no Authorization; internal calls attach the
// bridged Supabase JWT (mirror of src/modules/esign/lib.ts).
// ============================================================================

import { SUPABASE_ANON_KEY, getSupabaseToken } from '../../shared/supabase';

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sub-onboard`;

export interface PortalSteps {
  business_confirmed: boolean;
  coi_uploaded: boolean;
  w9_uploaded: boolean;
  direct_deposit_submitted: boolean;
  agreement_signed: boolean;
}

export interface PortalMeta {
  state: 'active' | 'verified' | 'completed' | 'expired';
  go_by_name: string | null;
  legal_business_name: string | null;
  recipient_email_masked: string;
  verified: boolean;
  otp_pending: boolean;
  steps: PortalSteps;
}

export interface IssueResult {
  ok: boolean;
  token_id: string;
  expires_at: string;
  delivery: { delivered: boolean; channel: string; reason?: string };
  portal_url?: string;
}

export class PortalError extends Error {
  status: number;
  code: string;
  constructor(status: number, code: string, message?: string) {
    super(message ?? code);
    this.status = status;
    this.code = code;
  }
}

async function call<T>(
  body: Record<string, unknown>,
  internal: boolean,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: SUPABASE_ANON_KEY,
  };
  if (internal) {
    const jwt = getSupabaseToken();
    if (!jwt) throw new PortalError(401, 'not_signed_in');
    headers.Authorization = `Bearer ${jwt}`;
  }
  const res = await fetch(FN_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    throw new PortalError(
      res.status,
      String(json.error ?? `http_${res.status}`),
      json.message ? String(json.message) : undefined,
    );
  }
  return json as T;
}

export function portalPublic<T>(body: Record<string, unknown>): Promise<T> {
  return call<T>(body, false);
}

export function portalInternal<T>(body: Record<string, unknown>): Promise<T> {
  return call<T>(body, true);
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}
